import { manufacturingFetch } from "@/lib/manufacturingClient";
import type {
  MfgDealerAddress,
  MfgDealerLocationCreateResponse,
  MfgDealerLocationsBulkSetPlantResponse,
  MfgDealerLocationUpdateResponse,
  MfgDealerSetPrimaryPlantResponse,
  MfgDealerSetServiceResaleResponse,
  MfgDealersListResponse,
  MfgSubscriberLocationsBulkSetPlantResponse,
  MfgSubscriberLocationsListResponse,
} from "./dealerTypes";

// D3l: client fetchers for the Logistics → Dealers panel. Uses the
// shared manufacturingFetch wrapper so auth + base-URL handling stays
// consistent with the rest of the mfg-* surface.

export function listManufacturingDealers() {
  return manufacturingFetch<MfgDealersListResponse>("/api/mfg/dealers", { method: "GET" });
}

export function setManufacturingDealerPrimaryPlant(payload: {
  dealer_org_id: string;
  // Explicit null to clear, string to set, omitted to leave alone.
  primary_plant_id?: string | null;
  notes?: string;
}) {
  return manufacturingFetch<MfgDealerSetPrimaryPlantResponse>(
    "/api/mfg/dealers/set-primary-plant",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
}

export function setManufacturingDealerServiceResale(payload: {
  subscriber_org_id: string;
  service_resale_enabled: boolean;
}) {
  return manufacturingFetch<MfgDealerSetServiceResaleResponse>(
    "/api/mfg/dealers/set-service-resale",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
}

export function createManufacturingDealerLocation(payload: {
  name: string;
  address?: MfgDealerAddress | Record<string, unknown>;
  primary_plant_id?: string | null;
  linked_dealer_org_id?: string | null;
  contact_name?: string;
  contact_phone?: string;
  contact_email?: string;
  notes?: string;
  is_active?: boolean;
}) {
  return manufacturingFetch<MfgDealerLocationCreateResponse>(
    "/api/mfg/dealer-locations/create",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
}

// Supply Network: bulk reassign dealer locations to a plant. `null`
// clears the per-location override (locations then "inherit" the org
// default in the UI).
export function bulkSetManufacturingDealerLocationsPlant(payload: {
  location_ids: string[];
  primary_plant_id: string | null;
}) {
  return manufacturingFetch<MfgDealerLocationsBulkSetPlantResponse>(
    "/api/mfg/dealer-locations/bulk-set-plant",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
}

// D3m: list app-using subscriber dealers' physical locations
// (public.locations rows) with the manufacturer's plant-assignment
// overlay merged in. Parallel to listManufacturingDealers, which
// covers non-app dealers.
export function listManufacturingSubscriberLocations() {
  return manufacturingFetch<MfgSubscriberLocationsListResponse>(
    "/api/mfg/subscriber-locations",
    { method: "GET" }
  );
}

// D3m: bulk reassign subscriber dealer locations to a manufacturer
// plant. `null` clears the per-location override (the row then falls
// through to inheriting from the subscriber-org default).
export function bulkSetManufacturingSubscriberLocationsPlant(payload: {
  location_ids: string[];
  primary_plant_id: string | null;
}) {
  return manufacturingFetch<MfgSubscriberLocationsBulkSetPlantResponse>(
    "/api/mfg/subscriber-locations/bulk-set-plant",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
}

export function updateManufacturingDealerLocation(payload: {
  location_id: string;
  name?: string;
  // Explicit null to clear, object to set, omitted to leave alone.
  address?: MfgDealerAddress | Record<string, unknown> | null;
  primary_plant_id?: string | null;
  linked_dealer_org_id?: string | null;
  contact_name?: string;
  contact_phone?: string;
  contact_email?: string;
  notes?: string;
  is_active?: boolean;
}) {
  return manufacturingFetch<MfgDealerLocationUpdateResponse>(
    "/api/mfg/dealer-locations/update",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
}
